import { CLIENT_ID, CLIENT_ID_HEADER } from "./clientId";
import { API_BASE } from "./config";
import { bounceIfUnauthorized } from "./session";
import type { DeckRow } from "./setup";

/**
 * A deck's preset, as Setup's デッキ panel edits it (日本-344). The server half is
 * `server/src/nihongonator/anki/deck_options.py`; every refusal the form draws is one the server
 * also makes, so a stale tab cannot get round a rule by skipping this file.
 */

export type LeechAction = "tag" | "suspend";
export type NewMix = "mix" | "after" | "before";

export interface DeckOptions {
  deckId: number;
  deck: string;
  presetId: number;
  preset: string;
  /** The save's expect-guard: a preset changed elsewhere since this was read is refused (409). */
  presetMtime: number;
  /** The OTHER decks on this preset — a save changes them too. */
  sharedWith: string[];
  newPerDay: number;
  reviewsPerDay: number;
  /** Minutes, as Anki stores them. */
  learnSteps: number[];
  relearnSteps: number[];
  leechThreshold: number;
  leechAction: string;
  newMix: string;
  /**
   * The vocab deck is on this preset, so the frequency-sort job owns new-card order and the three
   * ordering fields below are null. Keyed on the PRESET, not the deck: a deck sharing 単語's preset
   * would otherwise change 単語's queue.
   */
  orderManaged: boolean;
  insertOrder: string | null;
  gatherOrder: string | null;
  sortOrder: string | null;
  /** New, unsuspended cards in this deck — the runway line's numerator. */
  unseen: number;
  /** New cards across every deck on the preset: what a change of insertion order re-numbers. */
  newOnPreset: number;
  leechNotes: number;
}

/** What the form holds: the saved fields, with the two step lists as the text being typed. */
export interface DeckOptionsDraft {
  newPerDay: string;
  reviewsPerDay: string;
  learnSteps: string;
  relearnSteps: string;
  leechThreshold: string;
  leechAction: string;
  newMix: string;
  insertOrder: string | null;
  gatherOrder: string | null;
  sortOrder: string | null;
}

export type DeckOptionsPatch = Partial<{
  newPerDay: number;
  reviewsPerDay: number;
  learnSteps: number[];
  relearnSteps: number[];
  leechThreshold: number;
  leechAction: string;
  newMix: string;
  insertOrder: string;
  gatherOrder: string;
  sortOrder: string;
}>;

// --- steps ---------------------------------------------------------------------------------------

const UNIT_MINUTES: Record<string, number> = { s: 1 / 60, m: 1, h: 60, d: 1440 };

/**
 * `"1m 10m 1h"` → minutes, or the FIRST token that is not a step. Anki's own units — `s m h d` — and
 * a bare number is minutes, which is what Anki does with one too. Empty is a valid, empty ladder.
 */
export function parseSteps(text: string): { ok: true; minutes: number[] } | { ok: false; bad: string } {
  const minutes: number[] = [];
  for (const token of text.trim().split(/\s+/).filter(Boolean)) {
    const match = /^(\d+(?:\.\d+)?)([smhd]?)$/.exec(token);
    const value = match ? Number(match[1]) * (UNIT_MINUTES[match[2] || "m"] ?? NaN) : NaN;
    if (!match || !Number.isFinite(value) || value <= 0) return { ok: false, bad: token };
    minutes.push(value);
  }
  return { ok: true, minutes };
}

/** The inverse, in the largest unit that divides the step exactly. */
export function formatSteps(minutes: number[]): string {
  return minutes
    .map((value) => {
      if (value >= 1440 && value % 1440 === 0) return `${value / 1440}d`;
      if (value >= 60 && value % 60 === 0) return `${value / 60}h`;
      if (Number.isInteger(value)) return `${value}m`;
      return `${Math.round(value * 60)}s`;
    })
    .join(" ");
}

// --- the form ------------------------------------------------------------------------------------

export function draftOf(options: DeckOptions): DeckOptionsDraft {
  return {
    newPerDay: String(options.newPerDay),
    reviewsPerDay: String(options.reviewsPerDay),
    learnSteps: formatSteps(options.learnSteps),
    relearnSteps: formatSteps(options.relearnSteps),
    leechThreshold: String(options.leechThreshold),
    leechAction: options.leechAction,
    newMix: options.newMix,
    insertOrder: options.insertOrder,
    gatherOrder: options.gatherOrder,
    sortOrder: options.sortOrder,
  };
}

function wholeNumber(text: string): number | null {
  return /^\d+$/.test(text.trim()) ? Number(text.trim()) : null;
}

function sameSteps(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((value, index) => Math.abs(value - (b[index] ?? NaN)) < 1e-9);
}

export type DraftField = keyof DeckOptionsDraft;

export interface Diff {
  patch: DeckOptionsPatch;
  /** Fields that differ from what was saved — the `変更 N件` count and the brighter borders. */
  changed: DraftField[];
  /** Fields whose text cannot be sent at all. A step names its bad token. */
  invalid: Partial<Record<DraftField, string>>;
}

/**
 * Only what changed goes up. Out-of-range NUMBERS are sent as typed and refused by the server —
 * ReviewPanel's rule, so the form never quietly holds a value other than the one it shows. What
 * cannot be sent at all (a step that is not a step, a count that is not a number) blocks the save.
 */
export function diffOptions(saved: DeckOptions, draft: DeckOptionsDraft): Diff {
  const patch: DeckOptionsPatch = {};
  const changed: DraftField[] = [];
  const invalid: Diff["invalid"] = {};
  const base = draftOf(saved);

  for (const field of ["newPerDay", "reviewsPerDay", "leechThreshold"] as const) {
    if (draft[field].trim() === base[field]) continue;
    changed.push(field);
    const value = wholeNumber(draft[field]);
    if (value === null) invalid[field] = draft[field];
    else patch[field] = value;
  }
  for (const [field, savedSteps] of [
    ["learnSteps", saved.learnSteps],
    ["relearnSteps", saved.relearnSteps],
  ] as const) {
    const parsed = parseSteps(draft[field]);
    if (!parsed.ok) {
      changed.push(field);
      invalid[field] = parsed.bad;
    } else if (!sameSteps(parsed.minutes, savedSteps)) {
      changed.push(field);
      patch[field] = parsed.minutes;
    }
  }
  for (const field of ["leechAction", "newMix", "insertOrder", "gatherOrder", "sortOrder"] as const) {
    const value = draft[field];
    if (value === null || value === base[field]) continue;
    changed.push(field);
    patch[field] = value;
  }
  return { patch, changed, invalid };
}

/**
 * Days until the new queue runs dry — `stats/pace.runway`, ported so the line can follow the box as
 * it is typed into. **Floors, as the Python does**; `deckOptions.test.ts` pins the two together, and
 * if one changes the other must, or Setup and Pace disagree about one deck.
 */
export function runwayDays(unseen: number, newPerDay: number): number | null {
  if (newPerDay <= 0) return null;
  return Math.floor(unseen / newPerDay);
}

// --- the wire ------------------------------------------------------------------------------------

export type SaveResult =
  | { ok: true; options: DeckOptions; decks: DeckRow[] }
  /** A review sitting is open: saving would sync and clear its undo queue. */
  | { ok: false; kind: "sitting"; deck: string }
  /** The preset changed elsewhere since this form read it. */
  | { ok: false; kind: "stale" }
  /** A refused value — the server's own words. */
  | { ok: false; kind: "refused"; message: string };

async function request(method: string, path: string, body?: unknown): Promise<Response> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      [CLIENT_ID_HEADER]: CLIENT_ID,
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: "no-store",
  });
  bounceIfUnauthorized(res);
  return res;
}

async function detailOf(res: Response): Promise<unknown> {
  try {
    return (await res.json())?.detail;
  } catch {
    return undefined;
  }
}

export async function getDeckOptions(deckId: number): Promise<DeckOptions> {
  const res = await request("GET", `/api/setup/decks/${deckId}/options`);
  if (!res.ok) {
    const detail = await detailOf(res);
    throw new Error(typeof detail === "string" ? detail : `Request failed (${res.status})`);
  }
  return res.json();
}

export async function saveDeckOptions(
  deckId: number,
  expectMtime: number,
  patch: DeckOptionsPatch,
): Promise<SaveResult> {
  const res = await request("PUT", `/api/setup/decks/${deckId}/options`, { expectMtime, ...patch });
  if (res.ok) return { ok: true, ...(await res.json()) };
  const detail = await detailOf(res);
  if (res.status === 409 && typeof detail === "object" && detail !== null) {
    const reason = (detail as { reason?: string; deck?: string }).reason;
    if (reason === "sitting") return { ok: false, kind: "sitting", deck: String((detail as { deck?: string }).deck) };
    if (reason === "stale") return { ok: false, kind: "stale" };
  }
  if (res.status === 400 && typeof detail === "string") return { ok: false, kind: "refused", message: detail };
  throw new Error(typeof detail === "string" ? detail : `Request failed (${res.status})`);
}
